/**
 * Lightweight pub/sub bridge so non-React code (e.g. UserManager) can tell the
 * TeamMembersProvider to drop its cached roster after a mutation.
 *
 * The cached team-member list lives in TeamMembersContext (React state). Plain
 * modules can't call into a React context, so mutations call
 * `invalidateTeamMembers()` and the provider — which subscribes on mount —
 * refetches. This keeps invalidation in one place (UserManager) instead of
 * scattered across every component that triggers a user change.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Provider subscribes on mount; returns an unsubscribe for cleanup. */
export function subscribeTeamMembersInvalidation(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Call after any mutation that changes the team roster — user create, update,
 * delete, or role change. Notifies the provider to refetch the cached list.
 */
export function invalidateTeamMembers(): void {
  listeners.forEach((fn) => fn());
}
